/* eslint-disable import/no-cycle */ // TODO: {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4218}
import { useEffect, useState } from 'react';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import history from 'service/customHistory';
import Markdown from 'markdown-to-jsx';
import { some, useConditionalEffect, useDeltaArray } from 'react-delta';
import { useMutation } from '@apollo/client';
import { showWaitCursor } from 'components/common/Page/Page';

import FullScreenIcon from '@mui/icons-material/Fullscreen';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import CopyLink from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MoreIcon from '@mui/icons-material/MoreHoriz';
import LockIcon from '@mui/icons-material/Lock';
import UnlockIcon from '@mui/icons-material/LockOpen';
import { SvgIcon } from '@mui/material';
import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT, { createTestId } from 'common/element';
import { buildUrl } from 'common/util/location';
import { isSafari } from 'service/browser';
import { getDateRangeText, getFormattedTimeOffsetUTC, getTimezoneOffset, parseTimeRange } from 'common/util/time';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useRecoilCleanup } from 'hooks/useRecoilCleanup';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';
import { useShortUrl } from 'hooks/useShortUrl';
import { MenuItemConfig, UrlParams } from 'common/types';
import log from 'common/ScalyrLogger';
import useCopyToClipboard from 'hooks/useCopyToClipboard';

import Button from 'components/common/Button/Button';
import IconButton from 'components/common/IconButton/IconButton';
import IconMenu from 'components/common/IconButton/IconMenu';
import Dropdown from 'components/common/Dropdown/Dropdown';
import { IconTextPair } from 'components/common/DropdownItems/DropdownItems';
import TimeRangeDropdown from 'components/common/TimeRangeDropdown/TimeRangeDropdown';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import {
  absoluteResolvedUrlState,
  queryTimeRangeState,
  searchParamRecordState,
  searchParamState,
} from 'components/common/Managers/RouteStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { isFullScreenModeState } from 'components/other/MainNavBar/MainNavBar.state';

import AddEditPrefilterModal from 'components/dashboards/DashboardPage/modals/AddEditPrefilterModal';
import AddGraphModal, { GraphType } from 'components/dashboards/DashboardPage/modals/AddGraphModal';
import DashboardHeaderParams from 'components/dashboards/DashboardPage/DashboardHeaderParams';
import AddPanelDropdown from 'components/dashboards/DashboardPage/modals/AddPanelDropdown';
import DashboardSettings from 'components/dashboards/DashboardPage/modals/DashboardSettings';
import EditPanel from 'components/dashboards/DashboardPage/modals/EditPanel';
import SetColumns from 'components/dashboards/DashboardPage/modals/SetColumns';
import { ReactComponent as KubeSVG } from 'assets/img/logo-kubernetes.svg';

import {
  DashboardConfigType,
  FilterConfigType,
  GraphConfigType,
  ParameterConfigType,
} from 'components/dashboards/types';
import { DELETE_DASHBOARD, GET_CONFIGV2, LIST_DASHBOARD_NAMES, SAVE_LAYOUT } from 'components/dashboards/queries';

import './DashboardHeader.scss';
import Tab from 'components/common/Tab/Tab';
import TabList from 'components/common/TabList/TabList';
import Tabs from 'components/common/Tabs/Tabs';
import { isLayoutLocked, trimUnusedOptions } from '../dashboardData';
import { dashboardTabsState } from '../dashboards.state';
import AddEditTabsModal from './modals/AddEditTabsModal';

/**
 * Global state for tracking the number of minutes the client should wait between dashboard refresh queries.
 * `0` means that the client should never refresh.
 */
export const autoRefreshMinutesState = atom<number>({
  key: K.RECOIL_ATOM_KEY.AUTO_REFRESH_MINUTES,
  default: 0,
});

type DashboardHeaderProps = {
  refetchDashboard?: () => void;
  dashboardName: string;
  dashboardConfig?: DashboardConfigType;
  graphIndex: number;
  params?: string;
  dashboardFilters?: FilterConfigType[];
  hasWriteAccess: boolean;
};

/**
 * Displays and handles all interactions for the non-grid (i.e., header) section of a Dashboard page.
 *
 * @prop refetchDashboard        Function that calls refetch() to refresh the dashboard.
 * @prop dashboardName           The dashboard config file name / title
 * @prop dashboardConfig         The dashboard's JSON configuration
 * @prop params                  A serialized list of key value pairs
 * @prop graphIndex              A graph index (0...) if we're viewing just one graph, or -1 if we're not
 */
function DashboardHeader({
  refetchDashboard = (): void => {},
  dashboardName,
  dashboardConfig,
  params,
  dashboardFilters = [],
  graphIndex,
  hasWriteAccess,
}: DashboardHeaderProps): JSX.Element {
  const isDashboardFiltersEnabled = useIsLabEnabled(K.LAB.DASHBOARD_FILTERS);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const dashboardTabs = useRecoilValue(dashboardTabsState);
  const activeTab = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const { startTime, endTime } = queryTimeRange;
  const setSearchParams = useSetSearchParams();
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  // TODO: `searchParams` is a temporary state dependency for a hack. The Dashboard page is very sensitive to unnecessary state dependencies. Remove as part of {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5623}
  const searchParams = useRecoilValue(searchParamRecordState);

  const [isFullScreenMode, setIsFullScreenMode] = useRecoilState(isFullScreenModeState);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const timezone = useRecoilValue(timezoneState);
  const timezoneOffset = getTimezoneOffset(timezone);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showEditGraph, setShowEditGraph] = useState(false);
  const [showSetColumns, setShowSetColumns] = useState(false);
  const [showAddPanelDropdown, setShowAddPanelDropdown] = useState(false);
  const [showAddStandardGraph, setShowAddStandardGraph] = useState(false);
  const [showAddBreakdownGraph, setShowAddBreakdownGraph] = useState(false);
  const [showAddPieChart, setShowAddPieChart] = useState(false);
  const [showAddTablePanel, setShowAddTablePanel] = useState(false);
  const [showAddNumberPanel, setShowAddNumberPanel] = useState(false);
  const [showAddMarkdownPanel, setShowAddMarkdownPanel] = useState(false);
  const [showAddDonutPanel, setShowAddDonutPanel] = useState(false);
  const [showAddHoneyCombPanel, setShowAddHoneyCombPanel] = useState(false);
  const [showPrefilterModal, setShowPrefilterModal] = useState(false);
  const [showAddEditTabsModal, setShowAddEditTabsModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [previousShortenedUrl, setPreviousShortenedUrl] = useState<string>('');
  const [copyLinkIsAvailable, setCopyLinkIsAvailable] = useState(!isSafari);
  const { fetchShortUrlPromise } = useShortUrl();
  const copy = useCopyToClipboard();

  const [autoRefreshMinutes, setAutoRefreshMinutes] = useRecoilState(autoRefreshMinutesState);
  const autoRefreshChanged = some(useDeltaArray([isFullScreenMode, autoRefreshMinutes]));

  const [doDeleteDashboard] = useMutation(DELETE_DASHBOARD, {
    refetchQueries: [{ query: LIST_DASHBOARD_NAMES }],
  });

  const [doSaveLayout] = useMutation(SAVE_LAYOUT, {
    refetchQueries: [
      {
        query: GET_CONFIGV2,
        variables: { name: dashboardName, startTime, endTime },
      },
    ],
  });

  // --------------------------------------------------------------------------------------------------------------- FULL SCREEN AUTO REFRESH

  useConditionalEffect(() => {
    let timeoutPromise: ReturnType<typeof setInterval>;
    if (isFullScreenMode && autoRefreshMinutes > 0) {
      timeoutPromise = setInterval(() => {
        setSearchParams({ ...searchParams } as UrlParams, true); // TODO: unnecessary hack to be removed by {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5623}
      }, autoRefreshMinutes * K.MS_PER_MIN);
    }

    return (): void => {
      if (timeoutPromise) {
        log.debug('Dashboard autoRefresh timeout cleanup');
        clearTimeout(timeoutPromise);
      }
    };
  }, autoRefreshChanged);

  // --------------------------------------------------------------------------------------------------------------- ESC EXIT FULL SCREEN

  useEffect(() => {
    function handleKeyUp(ev: any): void {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      if (ev.key === 'Escape') {
        setIsFullScreenMode(false);
      }
    }
    if (isFullScreenMode) {
      window.addEventListener('keyup', handleKeyUp);
    }

    return (): void => window.removeEventListener('keyup', handleKeyUp);
  }, [isFullScreenMode, setIsFullScreenMode]);

  // --------------------------------------------------------------------------------------------------------------- UNMOUNT CLEAN UP

  useRecoilCleanup(isFullScreenModeState, autoRefreshMinutesState);

  // --------------------------------------------------------------------------------------------------------------- NO DATA YET

  if (!dashboardConfig) {
    return <></>;
  }

  // --------------------------------------------------------------------------------------------------------------- HAS DATA NOW

  const isGraphView = graphIndex >= 0 && graphIndex < dashboardConfig.graphs.length;
  const graphConfig = isGraphView ? dashboardConfig.graphs[graphIndex] : undefined;

  // --------------------------------------------------------------------------------------------------------------- DELETE DASHBOARD MUTATION

  const deleteDashboard = (): void => {
    // Delete the dashboard's config file then redirect to the home page
    doDeleteDashboard({
      variables: {
        dashboardName,
        tabName: dashboardConfig.tabName,
      },
    })
      .then(() => {
        history.replace({
          pathname: K.ROUTE.ROOT,
        });
      })
      .catch((error) => {
        setPageAlertConfig({ message: error.message, variant: PageAlertVariant.ERROR });
      });
  };

  // --------------------------------------------------------------------------------------------------------------- SHARED BUTTONS

  const timePicker = (
    <div key="timeRangeButton" className="DashboardHeader__TimePicker">
      <TimeRangeDropdown />
    </div>
  );

  const refreshButton = (
    <IconButton
      key="refetchButton"
      onClick={(): void => {
        setSearchParams({ ...searchParams } as UrlParams, true); // TODO: unnecessary hack to be removed by {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5623}
      }}
      tooltip="Refresh Dashboard"
      icon={<RefreshIcon />}
      testId={ELEMENT.dashboardHeaderRefresh}
    />
  );

  // --------------------------------------------------------------------------------------------------------------- TIME RANGE

  const { startTime: actualStartTime, endTime: actualEndTime } = parseTimeRange(queryTimeRange, timezone);
  let formattedTime = '';
  if (actualStartTime && actualEndTime) {
    formattedTime =
      getDateRangeText(actualStartTime.milliseconds, actualEndTime.milliseconds, timezone) +
      '  ' +
      getFormattedTimeOffsetUTC(timezoneOffset);
  }

  const controls = [];
  controls.push(
    <div key="timeRange" className="DashboardHeader__TimeRange">
      {formattedTime}
    </div>,
  );

  // --------------------------------------------------------------------------------------------------------------- VIEW GRAPH STATE

  if (graphConfig) {
    const query = graphConfig && (graphConfig as GraphConfigType).query;
    if (query) {
      controls.push(
        <Button
          key="exploreButton"
          href={buildUrl(K.ROUTE.POWER_QUERY, {
            [K.URL_PARAM.FILTER]: query,
            [K.URL_PARAM.START_TIME]: startTime,
            [K.URL_PARAM.END_TIME]: endTime,
          } as UrlParams)}
        >
          Explore Query
        </Button>,
      );
    } else if (hasWriteAccess) {
      controls.push(
        <Button
          key="editGraphButton"
          className="DashboardHeader__EditGraph"
          onClick={(): void => setShowEditGraph(true)}
          testId={ELEMENT.dashboardEditGraph}
        >
          Edit Graph
        </Button>,
      );
    }

    const backToDashboardLink = buildUrl(K.ROUTE.DASHBOARD, {
      ...searchParams,
      [K.URL_PARAM.DASHBOARD_NAME]: dashboardName,
      [K.URL_PARAM.DASHBOARD_VIEW_GRAPH]: '',
    } as UrlParams);

    const returnToDashboard = (
      <div className="DashboardHeader__Exit" key="exitGraphView">
        <IconButton
          href={backToDashboardLink}
          tooltip="Back to Dashboard"
          className="DashboardHeader__ExitButton"
          icon={<CloseIcon />}
          testId={ELEMENT.dashboardHeaderClose}
        />
      </div>
    );

    if (!isDashboardFiltersEnabled) {
      controls.push(timePicker);
    }
    controls.push(refreshButton);
    controls.push(returnToDashboard);

    // --------------------------------------------------------------------------------------------------------------- FULL SCREEN STATE
  } else if (isFullScreenMode) {
    const REFRESH_INTERVALS: { [key: string]: string } = {
      0: 'Never Refresh',
      1: '1 min',
      5: '5 min',
      10: '10 min',
      15: '15 min',
      30: '30 min',
      45: '45 min',
      60: '1 hour',
      120: '2 hours',
    };

    const refreshMenu = [
      Object.entries(REFRESH_INTERVALS).map(([minutes, minuteText]) => ({
        text: minuteText,
        clickHandler: (): void => setAutoRefreshMinutes(Number(minutes)),
        testId: createTestId(ELEMENT.dashboardHeaderRefreshInterval, Number(minutes)),
      })),
    ];

    const refreshDropdown = (
      <Dropdown
        key="refreshButton"
        className="DashboardHeader__RefreshInterval"
        sections={refreshMenu}
        size="med"
        testId={ELEMENT.dashboardHeaderRefresh}
        alignRight
      >
        {autoRefreshMinutes === 0
          ? REFRESH_INTERVALS[autoRefreshMinutes]
          : `Refresh every ${REFRESH_INTERVALS[autoRefreshMinutes]}`}
      </Dropdown>
    );

    const exitFullScreen = (
      <div className="DashboardHeader__Exit" key="exitFullscreen">
        <IconButton
          onClick={(): void => {
            setIsFullScreenMode(false);
          }}
          tooltip="Exit Full Screen"
          className="DashboardHeader__ExitButton"
          icon={<CloseIcon />}
          testId={ELEMENT.dashboardHeaderFullScreenExit}
        />
      </div>
    );

    if (!isDashboardFiltersEnabled) {
      controls.push(timePicker);
    }
    controls.push(refreshDropdown);
    controls.push(exitFullScreen);

    // --------------------------------------------------------------------------------------------------------------- DEFAULT STATE
  } else {
    const copyLinkHandler = (): void => {
      if (isSafari) {
        if (!copyLinkIsAvailable) {
          return;
        }
        if (shortUrl) {
          copy(shortUrl, 'link');
        } else {
          setPageAlertConfig({
            message: 'Could not copy link to clipboard',
            variant: PageAlertVariant.ERROR,
            autoHide: true,
          });
        }
      } else {
        const urlToShorten = absoluteUrl;
        fetchShortUrlPromise(urlToShorten).then(({ data }) => {
          copy(data?.compressUrl || '', 'link');
        });
      }
    };

    const moreMenuTopItems: Array<MenuItemConfig> = [
      {
        children: <IconTextPair leftIcon={<FullScreenIcon fontSize="small" />} label="View Full Screen" />,
        clickHandler: (): void => {
          setIsFullScreenMode(true);
        },
        testId: ELEMENT.dashboardHeaderFullScreenEnter,
      },
    ];

    if (hasWriteAccess) {
      moreMenuTopItems.push({
        href: buildUrl(K.ROUTE.CONFIG_FILE, {
          [K.URL_PARAM.PATH]: `/dashboards/${dashboardName}`,
          [K.URL_PARAM.ORIGINAL_URL]: window.location.href,
        } as UrlParams),
        children: <IconTextPair leftIcon={<CodeIcon fontSize="small" />} label="Edit JSON" />,
        testId: ELEMENT.dashboardHeaderEditJson,
      });
    }

    moreMenuTopItems.push({
      children: (
        <IconTextPair
          leftIcon={<CopyLink fontSize="small" />}
          label={copyLinkIsAvailable ? 'Copy Link' : 'Preparing Link'}
        />
      ),
      clickHandler: copyLinkHandler,
      disabled: !copyLinkIsAvailable,
    });
    moreMenuTopItems.push({
      href: HELP_ROUTE.HELP_DASHBOARDS,
      children: <IconTextPair leftIcon={<HelpIcon fontSize="small" />} label="Help" />,
      testId: ELEMENT.dashboardHeaderHelp,
    });

    const moreMenuItems = [moreMenuTopItems];
    if (!dashboardConfig.options.layout.fixed && hasWriteAccess) {
      const moreMenuBottomItems = dashboardConfig.options.layout.locked
        ? []
        : [
            {
              children: <IconTextPair leftIcon={<ViewModuleIcon fontSize="small" />} label="Reset Layout…" />,
              clickHandler: (): void => setShowSetColumns(true),
              testId: ELEMENT.dashboardHeaderReset,
            },
          ];

      const lockIcon = isLayoutLocked(dashboardConfig) ? (
        <UnlockIcon fontSize="small" />
      ) : (
        <LockIcon fontSize="small" />
      );
      const lockText = isLayoutLocked(dashboardConfig) ? 'Unlock Layout' : 'Lock Layout';
      moreMenuBottomItems.push({
        children: <IconTextPair leftIcon={lockIcon} label={lockText} />,
        testId: ELEMENT.dashboardHeaderLock,
        clickHandler: (): void => {
          showWaitCursor(true);
          const layout = { ...dashboardConfig.options.layout, locked: dashboardConfig.options.layout.locked ? 0 : 1 };
          const options = trimUnusedOptions({ ...dashboardConfig.options, layout });
          doSaveLayout({
            variables: {
              name: dashboardName,
              options: JSON.stringify(options, null, 2),
              tabName: dashboardConfig.tabName,
            },
          }).then(() => {
            refetchDashboard();
          });
        },
      });
      moreMenuItems.push(moreMenuBottomItems);
    }

    if (!dashboardConfig.isBuiltIn && hasWriteAccess) {
      moreMenuItems.push([
        {
          children: <IconTextPair leftIcon={<EditIcon fontSize="small" />} label="Rename dashboard" />,
          clickHandler: (): void => setShowRename(true),
          testId: ELEMENT.dashboardHeaderRename,
        },
        {
          children: <IconTextPair leftIcon={<DeleteIcon fontSize="small" />} label="Delete dashboard" />,
          clickHandler: (): void => setConfirmDelete(true),
          testId: ELEMENT.dashboardHeaderDelete,
        },
      ]);
    }

    const onMoreActionsEntered = (): void => {
      if (isSafari) {
        const urlToShorten = absoluteUrl;
        if (previousShortenedUrl !== urlToShorten) {
          setCopyLinkIsAvailable(false);
          fetchShortUrlPromise(urlToShorten).then(({ data }) => {
            setShortUrl(data?.compressUrl || '');
            setPreviousShortenedUrl(urlToShorten);
            setCopyLinkIsAvailable(true);
          });
        }
      }
    };

    const addPanelDropdown = (
      <AddPanelDropdown
        hidePanelDropdown={(): void => setShowAddPanelDropdown(false)}
        showAddStandardGraph={(): void => {
          setShowAddStandardGraph(true);
        }}
        showAddBreakdownGraph={(): void => {
          setShowAddBreakdownGraph(true);
        }}
        showAddPieChart={(): void => {
          setShowAddPieChart(true);
        }}
        showAddTablePanel={(): void => {
          setShowAddTablePanel(true);
        }}
        showAddNumberPanel={(): void => {
          setShowAddNumberPanel(true);
        }}
        showAddMarkdownPanel={(): void => {
          setShowAddMarkdownPanel(true);
        }}
        showAddDonutPanel={(): void => {
          setShowAddDonutPanel(true);
        }}
        showAddHoneycombPanel={(): void => {
          setShowAddHoneyCombPanel(true);
        }}
        showAddTabsPanel={(): void => {
          setShowAddEditTabsModal(true);
        }}
        showAddEditDashboardFilters={
          dashboardFilters.length > 0 ? () => setIsEditMode(true) : () => setShowPrefilterModal(true)
        }
        hasWriteAccess={hasWriteAccess}
      />
    );

    const iconMenus = (
      <div className="DashboardHeader__IconMenus" key="controlMenus">
        {hasWriteAccess && (
          <OverlayTrigger
            placement="bottom-end"
            overlay={addPanelDropdown}
            showOverlay={showAddPanelDropdown}
            setShowOverlay={setShowAddPanelDropdown}
          >
            <IconButton tooltip="Add" icon={<AddIcon />} testId={ELEMENT.dashboardHeaderAddMenu} />
          </OverlayTrigger>
        )}
        <IconMenu
          tooltip="More Actions"
          sections={moreMenuItems as unknown as [][]}
          icon={<MoreIcon />}
          testId={ELEMENT.dashboardHeaderMore}
          onEntered={onMoreActionsEntered}
          placement="bottom-end"
        />
      </div>
    );

    controls.push(iconMenus);
    if (!isDashboardFiltersEnabled) {
      controls.push(timePicker);
    }
    controls.push(refreshButton);
  }

  // --------------------------------------------------------------------------------------------------------------- DASHBOARD/GRAPH NAME

  // TBD remove hardcoded dashboard name check to display icon and make it generic for k8s dashboards
  const dashboardNameControls = isGraphView ? (
    <div className="DashboardHeader__Title--Graph" data-test-id={ELEMENT.dashboardHeaderTitle}>
      {(graphConfig as GraphConfigType).title || (graphConfig as GraphConfigType).label || 'Untitled'}
    </div>
  ) : (
    <div className="DashboardHeader__Title" data-test-id={ELEMENT.dashboardHeaderTitle}>
      <>
        {dashboardName === 'Kubernetes Explorer' && (
          <SvgIcon className="DashboardHeader__Title--Icon">
            <KubeSVG />
          </SvgIcon>
        )}
        <span role="presentation" onClick={(): void => setShowRename(true)}>
          {dashboardName}
        </span>
      </>
    </div>
  );

  const onTabSelect = (tab: string) => {
    setSearchParams({
      [K.URL_PARAM.DASHBOARD_ACTIVE_TAB]: tab,
    } as UrlParams);
  };

  const dashboardTabsContent = (
    <Tabs activeTab={activeTab} onSelect={(tab) => onTabSelect(tab)} mountOnEnter={true}>
      <TabList className="TabList__Tabs DashboardHeader__Tabs">
        {dashboardTabs?.map((tab) => (
          <Tab key={tab} eventKey={tab}>
            {tab}
          </Tab>
        ))}
      </TabList>
      <IconButton
        className="DashboardHeader__EditTabsBtn"
        tooltip="Edit Tabs"
        onClick={() => setShowAddEditTabsModal(true)}
        icon={<EditIcon fontSize="small" />}
      />
    </Tabs>
  );

  const description = isGraphView ? '' : dashboardConfig.description;
  const hasParamsOrFilters =
    (dashboardConfig.parameters && dashboardConfig.parameters.length > 0) || dashboardFilters?.length > 0;

  // --------------------------------------------------------------------------------------------------------------- COMPONENT READY

  return (
    <div className="DashboardHeader">
      <div
        className="DashboardHeader__LayoutNotification DashboardHeader__LayoutNotification--Hide"
        id="DashboardLayoutNotification"
      />

      <div className="DashboardHeader__TopToolbar">
        {dashboardNameControls}
        {dashboardTabs && !isGraphView && dashboardTabsContent}
        <div className="DashboardHeader__Controls">{controls}</div>
      </div>

      {description && description?.length > 0 && (
        <div className="DashboardHeader__Desc">
          <Markdown>{description}</Markdown>
        </div>
      )}

      {hasParamsOrFilters && (
        <DashboardHeaderParams
          dashboardName={dashboardName}
          dashboardParams={dashboardConfig.parameters as ParameterConfigType[]}
          paramValues={params}
          dashboardFilters={dashboardFilters}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Please Confirm"
          onSubmit={deleteDashboard}
          show={confirmDelete}
          close={(): void => setConfirmDelete(false)}
          text="Are you sure you want to delete this dashboard?"
          cannotBeUndone
        />
      )}

      {showSetColumns && (
        <SetColumns
          showModal={showSetColumns}
          dashboardConfig={dashboardConfig}
          refetchDashboard={refetchDashboard}
          handleClose={(): void => setShowSetColumns(false)}
        />
      )}

      {showEditGraph && (
        <AddGraphModal
          isAddNew={false}
          dashboardName={dashboardName}
          graphType={(graphConfig as GraphConfigType).breakdownFacet ? GraphType.BREAKDOWN : GraphType.STANDARD}
          graphConfig={graphConfig as GraphConfigType}
          showModal={showEditGraph}
          handleClose={(): void => setShowEditGraph(false)}
          graphIndex={graphIndex}
        />
      )}

      {showAddStandardGraph && (
        <AddGraphModal
          isAddNew
          dashboardName={dashboardName}
          graphType={GraphType.STANDARD}
          showModal={showAddStandardGraph}
          handleClose={(): void => setShowAddStandardGraph(false)}
        />
      )}

      {showAddBreakdownGraph && (
        <AddGraphModal
          isAddNew
          dashboardName={dashboardName}
          graphType={GraphType.BREAKDOWN}
          showModal={showAddBreakdownGraph}
          handleClose={(): void => setShowAddBreakdownGraph(false)}
        />
      )}

      {showAddPieChart && (
        <EditPanel
          dashboardName={dashboardName}
          incomingConfig={{
            graphStyle: 'pie',
          }}
          showModal={showAddPieChart}
          handleClose={(): void => setShowAddPieChart(false)}
          panelIndex={-1}
        />
      )}

      {showAddTablePanel && (
        <EditPanel
          dashboardName={dashboardName}
          incomingConfig={{
            graphStyle: 'table',
          }}
          showModal={showAddTablePanel}
          handleClose={(): void => setShowAddTablePanel(false)}
          panelIndex={-1}
        />
      )}

      {showAddNumberPanel && (
        <EditPanel
          dashboardName={dashboardName}
          incomingConfig={{
            graphStyle: 'number',
          }}
          showModal={showAddNumberPanel}
          handleClose={(): void => setShowAddNumberPanel(false)}
          panelIndex={-1}
        />
      )}

      {showAddMarkdownPanel && (
        <EditPanel
          dashboardName={dashboardName}
          incomingConfig={{
            graphStyle: 'markdown',
          }}
          showModal={showAddMarkdownPanel}
          handleClose={(): void => setShowAddMarkdownPanel(false)}
          panelIndex={-1}
        />
      )}

      {showAddHoneyCombPanel && (
        <EditPanel
          dashboardName={dashboardName}
          incomingConfig={{
            graphStyle: K.PANEL_TYPE.HONEYCOMB,
          }}
          showModal={showAddHoneyCombPanel}
          handleClose={(): void => setShowAddHoneyCombPanel(false)}
          panelIndex={-1}
        />
      )}

      {showAddDonutPanel && (
        <EditPanel
          dashboardName={dashboardName}
          incomingConfig={{
            graphStyle: 'donut',
          }}
          showModal={showAddDonutPanel}
          handleClose={(): void => setShowAddDonutPanel(false)}
          panelIndex={-1}
        />
      )}

      {showAddEditTabsModal && (
        <AddEditTabsModal
          showModal={showAddEditTabsModal}
          handleClose={() => setShowAddEditTabsModal(false)}
          dashboardName={dashboardName}
        />
      )}

      {showPrefilterModal && (
        <AddEditPrefilterModal
          showModal={showPrefilterModal}
          handleClose={() => setShowPrefilterModal(false)}
          dashboardName={dashboardName}
        />
      )}

      <DashboardSettings
        showModal={showRename}
        dashboardName={dashboardName}
        dashboardDesc={dashboardConfig.description || ''}
        handleClose={(): void => setShowRename(false)}
      />
    </div>
  );
}

const getNotifier = (): HTMLElement | null => window.document.getElementById('DashboardLayoutNotification');
const setNotifierStyle = (notifier: HTMLElement, visibility: string): void => {
  notifier.className = `DashboardHeader__LayoutNotification DashboardHeader__LayoutNotification--${visibility}`;
};

export const showNotification = (message: string): void => {
  const notifier = getNotifier();
  if (notifier) {
    setNotifierStyle(notifier, 'Show');
    notifier.textContent = message;
  }
};

export const hideNotification = (): void => {
  const notifier = getNotifier();
  if (notifier) {
    setNotifierStyle(notifier, 'Hide');
  }
};

export default DashboardHeader;
